import { useState, useRef, useCallback } from 'react';
import { generateImage2 } from '../api/API';
import { useTaskPolling } from './useTaskPolling';

import formSchema from '../data/formSchema.json';
import formSchemaVideo from '../data/formSchemaVideo.json';

function getSchema(type) {
    return type === "video" ? formSchemaVideo : formSchema;
}

function getCfg(formData, textPrompt = null) {
    const schema = getSchema(formData.type || "photo");

    // If text prompt is provided, use simplified config
    if (textPrompt) {
        return {
            type: formData.type,
            batch_size: formData.number,
            action: "solo",
            fast_mode: formData.fastMode,
            prompt: { prompt_type: "text", data: { text: textPrompt } }
        };
    }

    // Original options-based config
    let ordered = { ...schema["stage1"].options[formData["stage1"]].data };
    let prompt = {};
    if (schema["stage1"].options[formData["stage1"]].data && Object.hasOwn(schema["stage1"].options[formData["stage1"]].data, "prompt")) {
        prompt = { ...prompt, ...schema["stage1"].options[formData["stage1"]].data.prompt }
    }
    let next = schema["stage1"].options[formData["stage1"]].next;
    while (true) {
        if (next === "generate") {
            break;
        }
        const option = schema[next].options[formData[next]].data;
        if (option) {
            ordered = { ...ordered, ...option };
            if (Object.hasOwn(option, "prompt")) {
                prompt = { ...prompt, ...option.prompt }
            }
        }
        next = schema[next].options[formData[next]].next;
    }
    ordered.type = formData.type;
    ordered.number = formData.number;
    ordered.batch_size = formData.number;
    ordered.fast_mode = formData.fastMode;
    ordered.prompt = { prompt_type: "options", data: prompt }
    ordered.raw = formData
    return ordered;
}

/**
 * Custom hook for photo/video generation logic
 *
 * @param {Object} options - Hook options
 * @param {string} options.token - Auth token
 * @param {Object} options.formRef - Ref containing form data
 * @param {boolean} options.textPromptActive - Whether text prompt mode is active
 * @param {string} options.textPromptValue - The text prompt value
 * @param {Object} options.callbacks - Callback functions
 *   - setGenType(type): Set generation type ('photo'|'video'|null)
 *   - setGenerated(bool): Set generated state
 *   - setImg(url): Set image result
 *   - setVideo(url): Set video result
 *   - setTaskId(id): Set task ID
 *   - setAnimateAction(action): Set animate action
 *   - setGenerationConfig(cfg): Store generation config
 *   - setShowVideoPopUp(bool): Show video popup
 *   - setShowPayPopUp(bool): Show payment popup
 *   - onScrollToResults(): Scroll to results area
 *   - onResetInteraction(): Reset user interaction flag
 * @returns {Object} - { handleGenerate, error, setError, cfgRef }
 */
export function usePhotoGenerate({
    token,
    formRef,
    textPromptActive,
    textPromptValue,
    callbacks
}) {
    const [error, setError] = useState(null);
    const cfgRef = useRef(null);

    const {
        setGenType,
        setGenerated,
        setImg,
        setVideo,
        setTaskId,
        setAnimateAction,
        setGenerationConfig,
        setShowVideoPopUp,
        setShowPayPopUp,
        onScrollToResults,
        onResetInteraction
    } = callbacks;

    // Initialize task polling hook for generation
    const { startPolling } = useTaskPolling(token, {
        onSuccess: (status) => {
            const url = status.result.output.message;
            if (cfgRef.current?.type === "photo") {
                const preload = new Image();
                const reveal = () => {
                    setImg(url);
                    setGenType(null);
                    setGenerated(true);
                };
                preload.onload = reveal;
                preload.onerror = reveal;
                preload.src = url;
            } else {
                setGenType(null);
                setGenerated(true);
                setVideo(url);
                if (setTaskId) {
                    setTaskId(null);
                }
            }
        },
        onError: (errorStatus) => {
            setGenType(null);
            if (errorStatus === "error") {
                setError(`Generation failed: "Unknown error"`);
            } else if (errorStatus === "verification_failed") {
                setError("Verification failed: The image may contain inappropriate content.");
            } else if (errorStatus === "parent_failed") {
                setError("Generation failed: A required step in the process failed.");
            }
        },
        onPollingError: () => {
            setGenType(null);
            setError("Error checking generation status. Please try again.");
        },
        onAuthError: () => {
            console.error('[usePhotoGenerate] Auth error during generation polling');
            setGenType(null);
            setError("Session expired. Please refresh the page and log in again.");
        }
    });

    const handleGenerate = useCallback(async () => {
        setError(null);

        try {
            // Pass text prompt if in text mode, otherwise use options
            const cfg = textPromptActive
                ? getCfg(formRef.current, textPromptValue)
                : getCfg(formRef.current);

            // Store cfg in ref for use in polling callbacks
            cfgRef.current = cfg;

            // Store generation config for AnimationPopup
            if (setGenerationConfig) {
                setGenerationConfig(cfg);
            }

            setGenerated(false);
            // Reset interaction flag to prevent auto-scroll to generate button after generation
            if (onResetInteraction) {
                onResetInteraction();
            }
            if (cfg.type === "photo") {
                setGenType("photo");
            } else {
                setGenType("video");
            }
            // Scroll to results area when generation starts
            if (onScrollToResults) {
                setTimeout(() => {
                    onScrollToResults();
                }, 50);
            }
            const [task, statusGeneration] = await generateImage2(token, cfg);

            if (statusGeneration === 400) {
                setGenType(null);
                setError("Generation already in progress, you can check results in your personal gallery");
                return;
            }

            if (statusGeneration === 402) {
                setGenType(null);
                if (cfgRef.current?.type !== "photo") {
                    if (setShowVideoPopUp) setShowVideoPopUp(true);
                }
                if (setShowPayPopUp) setShowPayPopUp(true);
                return;
            }
            if (statusGeneration === 401) {
                setGenType(null);
                setError("Inappropriate content detected in prompt. Please revise your text.");
                return;
            }

            // Check for safety failure (text prompts only)
            if (statusGeneration === 200 && task.status === "error" && task.safety === false) {
                setGenType(null);
                setError("Inappropriate content detected in prompt. Please revise your text.");
                return;
            }

            // Store task_id and set animation action for photos
            if (setTaskId && task.task_id) {
                setTaskId(task.task_id);
            }
            if (setAnimateAction && cfg.type === "photo") {
                setAnimateAction("cumshot"); // Default action, configurable here
            }

            // Start polling for generation completion
            startPolling(task.task_id);

        } catch (err) {
            console.error("Error generating image:", err, "formRef:", JSON.stringify(formRef.current));
            setGenType(null);
            setError("Failed: " + (err.message || err));
        }
    }, [
        token,
        formRef,
        textPromptActive,
        textPromptValue,
        setGenType,
        setGenerated,
        setTaskId,
        setAnimateAction,
        setGenerationConfig,
        setShowVideoPopUp,
        setShowPayPopUp,
        onScrollToResults,
        onResetInteraction,
        startPolling
    ]);

    return {
        handleGenerate,
        error,
        setError,
        cfgRef
    };
}

// Export getCfg for testing purposes
export { getCfg };
